import { useEffect, useRef, useState } from 'react'
import { Chart, ChartConfiguration, ChartEvent, registerables } from 'chart.js'
import { BatteryData } from '../../types/charts'
import 'chartjs-adapter-luxon'
import { Series, SeriesGroup, SeriesOptions } from '../../utils/series'
import { viewportShift } from '../../utils/history'
import { maxOf, minOf } from '../../utils/math'
import zoomPlugin from 'chartjs-plugin-zoom'
import { Box, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { sprintf } from 'sprintf-js'
import { midpointLinePlugin } from './midpointLinePlugin'
import { ChartSummary } from './ChartSummary'
import { lang } from '../../utils/lang'
import { ScatterPlot } from '@mui/icons-material'
import { useApp } from '../../hooks/useApp'
import { MOBILE_SIZE } from '../../types/constants'
import { quarterHourBandsPlugin, quarterHourLabels } from './quarterHourBands'

// Trigger onLoadMore* when the visible window's edge is within this many
// indices of the data's leftmost / rightmost real point.
const LOAD_MORE_THRESHOLD = 5

Chart.register(zoomPlugin, ...registerables)

export interface BatteryHistoryChartProps {
  id: string
  spinner?: boolean
  data: BatteryData[]
  labels?: string[]
  options: SeriesOptions
  noLabels?: boolean
  vat?: number
  onClick?: () => void
  onLoadMoreLeft?: () => void
  onLoadMoreRight?: () => void
  onViewportChange?: (fromMs: number, toMs: number) => void
}

export function BatteryHistoryChart(props: BatteryHistoryChartProps): React.ReactElement {
  const { id, data, noLabels, options, spinner, onLoadMoreLeft, onLoadMoreRight, onViewportChange } = props
  const theme = useTheme()
  const app = useApp()
  const { t } = useTranslation()
  const [main, setMain] = useState('')
  const [soc, setSoc] = useState('')
  const [left, setLeft] = useState('')
  const [right, setRight] = useState('')
  const [target, setTarget] = useState('')
  const [leftTarget, setLeftTarget] = useState('')

  // Start time (epoch ms) of the leftmost group of the previous render, used
  // to work out how far the index-based viewport must move when data grows.
  const prevFirstGroupFromRef = useRef<number | null>(null)
  // Lives across useEffect re-runs — Chart.getChart(id) would return nothing
  // because the previous effect's cleanup destroys the chart before the next
  // effect runs, so we can't read the prior viewport from the chart itself.
  const viewportRef = useRef<{ min: number, max: number } | null>(null)
  // Track previous steps so we know when to invalidate the saved viewport
  // (index-space changes meaning when scale changes).
  const prevStepsRef = useRef<string | undefined>(undefined)

  const color = options.variant === 'secondary' ? theme.palette.secondary.main : theme.palette.primary.main

  // Update values for summary.
  const setGroup = (summaryPower: number, group: SeriesGroup | undefined): void => {
    if (!group) {
      setMain('—')
      setLeft('')
      setRight('')
    } else {
      if (summaryPower === null) {
        setMain('—')
        setLeft('—,— kW - —,— kW')
      } else {
        setMain(`${sprintf('%.1f', summaryPower / 1000).replace('.', ',')}`)
        const min = group.members[0]?.min_power || 0
        const max = group.members[0]?.max_power || 0
        setLeft(`${sprintf('%.1f kW', min / 1000).replace(/\./g, ',')} ... ${sprintf('%.1f kW', max / 1000).replace(/\./g, ',')}`)
      }
      setRight(lang.timeLabel(group.from, options.steps || 'hour'))
    }
  }

  const setTargetGroup = (result:number, group: SeriesGroup | undefined): void => {
    if (!group) {
      setTarget('—')
      setLeftTarget('')
    } else {
      if (result === null) {
        setTarget('—')
        setLeftTarget('—,— kW - —,— kW')
      } else {
        setTarget(`${sprintf('%.1f', result / 1000).replace('.', ',')}`)
        const min = group.members[0]?.min_target || 0
        const max = group.members[0]?.max_target || 0
        setLeftTarget(`${sprintf('%.1f kW', min / 1000).replace(/\./g, ',')} ... ${sprintf('%.1f kW', max / 1000).replace(/\./g, ',')}`)
      }
    }
  }

  const setSocValue = (summarySoc:number, group: SeriesGroup | undefined): void => {
    if (!group) {
      setSoc('')
    } else {
      if (summarySoc === null) {
        setSoc('—')
      } else {
        setSoc(`${sprintf('%.0f', Math.round(summarySoc)).replace('.', ',')}`)
      }
    }
  }

  useEffect(() => {
    // When scale changes, the prior viewport's indices belong to a different
    // bucket count and have no meaning — reset so we fall back to the new
    // initial from..to window.
    const stepsChanged = prevStepsRef.current !== undefined && prevStepsRef.current !== options.steps
    prevStepsRef.current = options.steps
    if (stepsChanged) {
      viewportRef.current = null
    }

    const { labels, result, groups, startIndex, endIndex } = Series.simplify(data, { ...options, key: 'power' })
    const { result: socResult, groups: socGroups } = Series.simplify(data, { ...options, key: 'soc' })
    const { result: targetResult, groups: targetGroup } = Series.simplify(data, { ...options, key: 'target' })

    const prevFirstGroupFrom = prevFirstGroupFromRef.current
    prevFirstGroupFromRef.current = groups.length > 0 ? groups[0].from : null

    // Helper to find the current midpoint and refresh the summary values.
    const refreshSummary = (chart: Chart): void => {
      const { min, max } = chart.scales.x
      const idx = Math.floor((min + max) / 2)
      setGroup(result[idx], groups[idx])
      setSocValue(socResult[idx], socGroups[idx])
      setTargetGroup(targetResult[idx], targetGroup[idx])
    }

    // Trigger loadMore in either direction when the user pans within a few
    // points of the data's leftmost / rightmost real bucket. Skip on the
    // empty-data placeholder.
    const maybeLoadMore = (chart: Chart): void => {
      if (data.length <= 2) return
      const min = chart.scales.x.min as number
      const max = chart.scales.x.max as number
      if (onLoadMoreLeft && min < LOAD_MORE_THRESHOLD) {
        onLoadMoreLeft()
      }
      if (onLoadMoreRight && groups.length > 0 && max > groups.length - 1 - LOAD_MORE_THRESHOLD) {
        onLoadMoreRight()
      }
    }

    // Translate the chart's index-based viewport back to a wall-clock time
    // range so the parent can use it as the anchor for scale switches.
    const reportViewport = (chart: Chart): void => {
      if (!onViewportChange || groups.length === 0) return
      const min = chart.scales.x.min as number
      const max = chart.scales.x.max as number
      const lastIdx = groups.length - 1
      const minIdx = Math.max(0, Math.min(lastIdx, Math.floor(min)))
      const maxIdx = Math.max(0, Math.min(lastIdx, Math.floor(max)))
      onViewportChange(groups[minIdx].from, groups[maxIdx].to)
    }

    // Power y axis
    const yMax = Math.ceil(maxOf(result))
    const yMin = Math.floor(minOf(result))

    // Target y axis
    const yMaxTarget = Math.ceil(maxOf(targetResult))
    const yMinTarget = Math.floor(minOf(targetResult))

    const usedLabels: string[] = labels as string[]
    const usedSocData: (number | null)[] = socResult
    const usedPowerData: (number | null)[] = result
    const usedTargetData: (number | null)[] = targetResult

    if (options.panX) {
      const add = Math.floor((endIndex - startIndex) / 2)
      for (let i = 0; i < add; i++) {
        usedLabels.push('')
        usedSocData.push(null)
        usedPowerData.push(null)
        usedTargetData.push(null)
      }
    }

    // 15-min view: replace cluttered :00/:15/:30/:45 labels with bare hour
    // numbers and add alternating background bands to keep hours readable.
    const finalLabels = options.steps === 'quarter'
      ? quarterHourLabels(usedLabels, groups)
      : usedLabels

    const config: ChartConfiguration = {
      type: 'line',

      data: {
        labels: finalLabels,
        datasets: [
          {
            type: 'line',
            data: usedSocData,
            label: t('Battery state-of-Charge ( % )'),
            showLine: true,
            cubicInterpolationMode: 'monotone',
            pointRadius: 0,
            borderWidth: 2,
            backgroundColor: '#F4E356',
            borderColor: '#F4E356',
            tension: 0.3,
            animations: { tension: { easing: 'linear' } },
            yAxisID: 'ySoc'
          },
          {
            type: 'line',
            data: usedPowerData.map(p => p === null ? null : -p),
            label: t('Battery power ( kW )'),
            showLine: true,
            cubicInterpolationMode: 'monotone',
            pointRadius: 0,
            borderWidth: 2,
            backgroundColor: '#09E7A4',
            borderColor: '#09E7A4',
            tension: 0.3,
            animations: { tension: { easing: 'linear' } },
            yAxisID: (yMinTarget < yMin || yMaxTarget > yMax) ? 'yTarget' : 'y'
          },
          {
            type: 'line',
            data: usedTargetData.map(t => t === null ? null : -t),
            label: t('Battery power command ( kW )'),
            showLine: true,
            cubicInterpolationMode: 'monotone',
            pointRadius: 0,
            borderWidth: 2,
            backgroundColor: '#009467',
            borderColor: '#009467',
            tension: 0.3,
            animations: { tension: { easing: 'linear' } },
            yAxisID: (yMinTarget < yMin || yMaxTarget > yMax) ? 'yTarget' : 'y'
          },
        ]
      },

      plugins: [
        midpointLinePlugin(theme.palette.chartMidpointLine.main),
        {
          id: 'refreshSummary',
          afterRender: (chart) => refreshSummary(chart)
        },
        ...(options.steps === 'quarter' ? [quarterHourBandsPlugin(groups)] : []),
      ],

      options: {
        animation: false,

        plugins: {
          tooltip: {
            enabled: false,
          },
          legend: {
            display: false,
            position: 'bottom'
          },
          zoom: {
            pan: {
              enabled: true,
              mode: 'x',
              onPanComplete: ({ chart }) => {
                refreshSummary(chart)
                viewportRef.current = {
                  min: chart.scales.x.min as number,
                  max: chart.scales.x.max as number,
                }
                reportViewport(chart)
                maybeLoadMore(chart)
              }
            },
          }
        },

        scales: {
          x: {
            min: ['minute', 'year'].includes(options.steps || '') ? startIndex : startIndex + 1, // Note: this value can affect the matching of the labels and the midline.
            max: ['month'].includes(options.steps || '') ? endIndex : endIndex + 1,
            ticks: {
              color: theme.palette.grey[400],
              maxRotation: 0,
              // For quarter scale only 1-in-4 labels are non-empty (hours at
              // :00). Default autoSkip would land its first tick anywhere
              // from index 0 to 3 depending on pan, so labels would blink
              // in and out as you scrolled. Drawing every tick keeps the
              // :00 labels stable; the rest are blank strings anyway.
              autoSkip: options.steps === 'quarter' ? false : undefined,
              font: {
                size: 10
              },
            },
            display: !noLabels
          },

          y: {
            display: !((yMinTarget < yMin || yMaxTarget > yMax)),
            min: (Math.abs(yMax) > Math.abs(yMin)) ? -Math.abs(Math.ceil(Math.abs(yMax) / 1000) * 1000) : -Math.abs(Math.ceil(Math.abs(yMin) / 1000) * 1000),
            max: (Math.abs(yMax) > Math.abs(yMin)) ? Math.abs(Math.ceil(Math.abs(yMax) / 1000) * 1000) : Math.abs(Math.ceil(Math.abs(yMin) / 1000) * 1000),
            grid: {
              color: '#dfdfdf',
              lineWidth: ({ tick }) => tick.value === 0 ? 0.5 : 0.075
            },
            ticks: {
              color: theme.palette.grey[400],
              display: true,
              autoSkip: false,
              minRotation: 0,
              callback: (value) => sprintf('%.1f', Math.abs(value as number / 1000)) + ' kW'
            },
            title: {
              display: false,
              text: t('kW')
            },
            position: 'left'
          },

          ySoc: {
            min: 0,
            max: 100,
            grid: {
              color: '#dfdfdf',
              lineWidth: 0,
            },
            ticks: {
              color: theme.palette.grey[400],
              minRotation: 0,
              callback: (value) => sprintf(`%.${0}f`, value as number) + '%'
            },
            title: {
              display: false,
              text: t('%')
            },
            position: 'right',
            display: !(app.mobileWidth < MOBILE_SIZE), // Hide on mobile devices
          },

          yTarget: {
            display: !!((yMinTarget < yMin || yMaxTarget > yMax)),
            min: (Math.abs(yMaxTarget) > Math.abs(yMinTarget)) ? -Math.abs(Math.ceil(Math.abs(yMaxTarget) / 1000) * 1000) : -Math.abs(Math.ceil(Math.abs(yMinTarget) / 1000) * 1000),
            max: (Math.abs(yMaxTarget) > Math.abs(yMinTarget)) ? Math.abs(Math.ceil(Math.abs(yMaxTarget) / 1000) * 1000) : Math.abs(Math.ceil(Math.abs(yMinTarget) / 1000) * 1000),
            grid: {
              color: '#dfdfdf',
              lineWidth: ({ tick }) => tick.value === 0 ? 1.2 : 0.075
            },
            ticks: {
              color: theme.palette.grey[400],
              display: true,
              autoSkip: false,
              minRotation: 0,
              callback: (value) => sprintf('%.1f', Math.abs(value as number / 1000)) + ' kW'
            },
            title: {
              display: false,
              text: t('kW')
            },
            position: 'left'
          },

          yState: {
            min: 0,
            max: 100,
            ticks: {
              color: theme.palette.grey[400],
              minRotation: 0,
              callback: (value) => {
                if (value === 100) return t('Option-Charge')
                if (value === 0) return t('Option-Discharge')
              }
            },
            title: {
              display: false,
              text: t('%')
            },
            position: 'left',
            display: !(app.mobileWidth < MOBILE_SIZE), // Hide on mobile devices
          }
        },

        onClick: (e: ChartEvent) => {
          props.onClick && props.onClick()
        },
      }
    }

    // Preserve the viewport across data growth, so that loading an older or
    // newer page keeps the user looking at the same moment in time. The shift
    // is derived from where the previously leftmost group ended up: prepended
    // pages move it right, appended pages do not move it at all. Without that
    // distinction, appending a page scrolled the view forward by the size of
    // the page. Without the preservation, an unrelated re-render (e.g.
    // /history/oldest resolving) would snap the chart back to from..to.
    const shift = viewportShift(prevFirstGroupFrom, groups)
    const saved = viewportRef.current
    if (saved !== null && shift >= 0 && config.options?.scales?.x) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const xScale = config.options.scales.x as any
      xScale.min = saved.min + shift
      xScale.max = saved.max + shift
      viewportRef.current = { min: xScale.min as number, max: xScale.max as number }
    }

    Chart.getChart(id)?.destroy()
    const currentChart = new Chart(
      document.getElementById(id) as HTMLCanvasElement,
      config as ChartConfiguration
    )

    // Tell the parent about the chart's initial viewport, so a scale switch
    // without any prior pan still has a meaningful anchor.
    reportViewport(currentChart)

    return () => {
      if (currentChart) {
        currentChart.destroy()
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, data, options, t, color, theme.palette.grey, noLabels, onLoadMoreLeft, onLoadMoreRight, onViewportChange, props])

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, mt: 3, ml: 4 }}>
        <Box>
          <ChartSummary sx={{ margin: 0 }} main={main} left={left} steps={options.steps} unit="kW" />
          <Typography sx={{ color: '#09E7A4', fontWeight: 600 }}>{t('Actual battery power')}</Typography>
        </Box>
        <Box>
          <ChartSummary sx={{ margin: 0 }} main={target} left={leftTarget} steps={options.steps} unit="kW" />
          <Typography sx={{ color: '#009467', fontWeight: 600 }}>{t('Power command')}</Typography>
        </Box>
        <Box>
          <ChartSummary sx={{ margin: 0 }} main={soc} steps={options.steps} unit="%" />
          <Typography sx={{ color: '#F4E356', fontWeight: 600 }}>{t('State-of-charge')}</Typography>
        </Box>
        <Typography sx={{ color: theme.palette.chartText.main, fontSize: '0.75rem', fontWeight: 400 }}>{right}</Typography>
      </Box>
      <Box sx={{
        display: spinner ? 'relative' : 'none',
        height: 0,
        pl: 4
      }} >
        <Box className="rotating-slow" sx={{ display: 'inline-block' }}>
          <ScatterPlot sx={{ color, fontSize: 100 }}/>
        </Box>
      </Box>
      <Box>
        {app.mobileWidth < MOBILE_SIZE && <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
          <Typography sx={{ color: theme.palette.chartText.contrastText, fontSize: '0.75rem', fontWeight: 400, textAlign: 'center' }}>
            {t('Option-Charge')}
          </Typography>
          <Typography sx={{ color: theme.palette.chartText.contrastText, fontSize: '0.75rem', fontWeight: 400, textAlign: 'center' }}>
            100%
          </Typography>
        </Box>}
        <canvas id={id}/>
        {app.mobileWidth < MOBILE_SIZE && <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ color: theme.palette.chartText.contrastText, fontSize: '0.75rem', fontWeight: 400, textAlign: 'center' }}>
            {t('Option-Discharge')}
          </Typography>
          <Typography sx={{ color: theme.palette.chartText.contrastText, fontSize: '0.75rem', fontWeight: 400, textAlign: 'center' }}>
            0%
          </Typography>
        </Box>}
      </Box>
    </Box>
  )
}
